import type { 
  AISettings, 
  AIAnalysisRequest, 
  AIAnalysisResult, 
  TrialStatus,
  StreamingAIServiceState
} from '$lib/types/ai';
import type { GrammarError, AIComment } from '$lib/types/grammar';
import { AIStateService } from './analysis/AIStateService';
import { AIClientManagerService } from './analysis/AIClientManagerService';
import { AIAccessControlService } from './analysis/AIAccessControlService';
import { AITextAnalysisService } from './analysis/AITextAnalysisService';
import { AIErrorAnalysisService } from './analysis/AIErrorAnalysisService';
import { AIStreamingService } from './analysis/AIStreamingService';
import { AICorrectionAnalysisService } from './analysis/AICorrectionAnalysisService';
import { AIUtilityService } from './analysis/AIUtilityService';

/**
 * AI Analysis Service for Svelte Application
 * Refactored to use composition pattern with focused services
 * Uses Svelte stores for reactive state management
 */
export class AIAnalysisService {
  // Core services
  private stateService: AIStateService;
  private clientManager: AIClientManagerService;
  private accessControl: AIAccessControlService;
  private streamingService: AIStreamingService;
  private correctionAnalysis: AICorrectionAnalysisService;
  private abortController: AbortController | null = null;
  
  // Expose state for backward compatibility
  public readonly state;
  
  constructor(private settings: AISettings) {
    // Initialize services
    this.stateService = new AIStateService();
    this.clientManager = new AIClientManagerService(settings);
    this.accessControl = new AIAccessControlService(settings);
    this.streamingService = new AIStreamingService(this.stateService);
    this.correctionAnalysis = new AICorrectionAnalysisService(this.streamingService);
    
    // Expose state store for component binding
    this.state = this.stateService.state;
  }

  /**
   * Initialize the AI client with current settings
   */
  async initializeClient(): Promise<void> {
    this.stateService.updateState({ isInitialized: false, lastError: null });
    
    try {
      await this.clientManager.initializeClient();
      
      this.stateService.updateState({ 
        isInitialized: true, 
        currentModel: this.clientManager.getModelName()
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // If initialization fails with model not found, try with default OpenRouter settings
      if (errorMessage.includes('model') || errorMessage.includes('not found')) {
        const fallbackSettings = {
          ...this.settings,
          provider: 'openrouter' as const,
          selectedModel: 'openai/gpt-oss-120b'
        };
        this.clientManager.updateSettings(fallbackSettings);
        
        try {
          await this.clientManager.initializeClient();
          this.stateService.updateState({ 
            isInitialized: true, 
            currentModel: this.clientManager.getModelName()
          });
          return; // Success with fallback
        } catch (fallbackError) {
          // Silent fail on fallback
        }
      }
      
      this.stateService.updateState({ 
        isInitialized: false, 
        lastError: errorMessage 
      });
      throw error;
    }
  }

  /**
   * Set trial status for usage limiting
   */
  setTrialStatus(trial?: TrialStatus): void {
    this.accessControl.setTrialStatus(trial);
  }

  /**
   * Set premium status
   */
  setPremiumStatus(isPremium: boolean): void {
    this.accessControl.setPremiumStatus(isPremium);
    this.clientManager.setPremiumStatus(isPremium);
  }

  /**
   * Analyze entire text for grammar errors (AI-only analysis)
   * @param text The text to analyze
   * @param useStreaming Whether to use streaming if available
   * @returns Array of found grammar errors with AI comments
   */
  async analyzeText(
    text: string,
    useStreaming: boolean = false
  ): Promise<{ errors: GrammarError[], comments: Map<string, AIComment> }> {
    // Cancel any ongoing analysis
    this.cancel();
    
    // Create new abort controller for this request
    this.abortController = new AbortController();
    
    this.stateService.updateState({ 
      isAnalyzing: true, 
      lastError: null,
      streaming: useStreaming ? { isStreaming: true, chunks: [], currentChunk: '', progress: 0 } : undefined
    });

    try {
      // Initialize development trial status if needed
      this.accessControl.initializeDevTrialStatus();
      
      // Check access permissions
      await this.accessControl.checkAccessPermissions();

      // Ensure client is initialized
      await this.clientManager.ensureClientInitialized();
      const aiClient = this.clientManager.getClient();
      
      if (!aiClient) {
        throw new Error('AI client is not available');
      }

      // Check if cancelled before making the request
      if (this.abortController.signal.aborted) {
        throw new Error('Analysis cancelled');
      }

      // Use AITextAnalysisService to perform the analysis
      const result = await AITextAnalysisService.analyzeText(text, aiClient, useStreaming);

      // Update usage tracking
      this.accessControl.updateUsageTracking();

      this.stateService.updateState({ 
        isAnalyzing: false,
        streaming: undefined
      });

      return result;
    } catch (error) {
      // Check if the error is due to cancellation
      if (error instanceof Error && error.message === 'Analysis cancelled') {
        this.stateService.updateState({ 
          isAnalyzing: false, 
          lastError: '분석이 취소되었습니다',
          streaming: undefined
        });
        // Return empty result for cancelled analysis
        return { errors: [], comments: new Map() };
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Analysis failed';
      this.stateService.updateState({ 
        isAnalyzing: false, 
        lastError: errorMessage,
        streaming: undefined
      });
      throw error;
    } finally {
      // Clean up abort controller
      this.abortController = null;
    }
  }

  /**
   * Helper method to find text position in the full text
   * @deprecated Use AIUtilityService.findTextPosition instead
   */
  private findTextPosition(text: string, searchText: string, approximatePosition: number): { start: number, end: number } {
    return AIUtilityService.findTextPosition(text, searchText, approximatePosition);
  }

  /**
   * Analyze a single grammar error and generate AI comment
   * @param error The grammar error to analyze
   * @param contextText The full text for context
   * @param useStreaming Whether to use streaming if available
   * @returns AI comment for the error
   */
  async analyzeGrammarError(
    error: GrammarError, 
    contextText: string, 
    useStreaming: boolean = false
  ): Promise<AIComment> {
    this.stateService.updateState({ 
      isAnalyzing: true, 
      lastError: null,
      streaming: useStreaming ? { isStreaming: true, chunks: [], currentChunk: '', progress: 0 } : undefined
    });

    try {
      // Check access permissions
      await this.accessControl.checkAccessPermissions();

      // Ensure client is initialized
      await this.clientManager.ensureClientInitialized();
      const aiClient = this.clientManager.getClient();
      
      if (!aiClient) {
        throw new Error('AI client is not available');
      }

      // Use AIErrorAnalysisService to perform the analysis
      const result = await AIErrorAnalysisService.analyzeGrammarError(
        error, 
        contextText, 
        aiClient, 
        useStreaming
      );

      // Update usage tracking
      this.accessControl.updateUsageTracking();

      this.stateService.updateState({ 
        isAnalyzing: false,
        streaming: undefined
      });

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Analysis failed';
      this.stateService.updateState({ 
        isAnalyzing: false, 
        lastError: errorMessage,
        streaming: undefined
      });
      throw error;
    }
  }

  /**
   * Get current model name
   */
  getCurrentModel(): string | null {
    return this.clientManager.getModelName();
  }
  
  /**
   * Get current provider
   */
  getCurrentProvider(): string {
    return this.settings.provider || 'openrouter';
  }
  
  /**
   * Analyze corrections using AI with optional streaming
   * @param request AI analysis request
   * @param useStreaming Whether to use streaming if available
   * @returns AI analysis results
   */
  async analyzeCorrections(request: AIAnalysisRequest, useStreaming: boolean = false): Promise<AIAnalysisResult[]> {
    this.stateService.updateState({ 
      isAnalyzing: true, 
      lastError: null,
      analysisResults: null,
      streaming: undefined,
      fallbackHistory: []
    });

    try {
      // Check access permissions
      await this.accessControl.checkAccessPermissions();
      
      // Ensure client is initialized
      await this.clientManager.ensureClientInitialized();
      const aiClient = this.clientManager.getClient();
      
      if (!aiClient) {
        throw new Error('AI client is not available');
      }

      // Estimate token usage before making request
      const estimatedTokens = AIUtilityService.estimateTokenUsage(request);
      this.stateService.updateState({ 
        tokenUsage: { estimated: estimatedTokens } 
      });

      // Use AICorrectionAnalysisService to perform the analysis
      const validatedResults = await this.correctionAnalysis.analyzeCorrections(
        request, 
        aiClient, 
        useStreaming
      );
      
      // Update usage tracking
      this.accessControl.updateUsageTracking();
      
      this.stateService.updateState({ 
        isAnalyzing: false,
        analysisResults: validatedResults,
        streaming: undefined
      });

      return validatedResults;
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Analysis failed';
      this.stateService.updateState({ 
        isAnalyzing: false, 
        lastError: errorMessage,
        streaming: undefined
      });
      throw error;
    }
  }

  /**
   * Check if analysis can be performed
   * @deprecated Use accessControl.canAnalyze() instead
   */
  private canAnalyze(): boolean {
    return this.accessControl.canAnalyze();
  }

  /**
   * Stream analysis helper method
   * @deprecated Use streamingService.streamAnalysis() instead
   */
  private async streamAnalysis(prompt: string): Promise<string> {
    const aiClient = this.clientManager.getClient();
    if (!aiClient) {
      throw new Error('AI client not initialized');
    }
    
    return await this.streamingService.streamAnalysis(prompt, aiClient);
  }

  /**
   * Check if AI service is available
   */
  async isAvailable(): Promise<boolean> {
    // Initialize development trial status if needed
    this.accessControl.initializeDevTrialStatus();
    
    // Check access permissions
    const accessStatus = this.accessControl.getAccessStatus();
    if (!accessStatus.allowed) {
      return false;
    }
    
    try {
      // Ensure client is initialized
      await this.clientManager.ensureClientInitialized();
      return await this.clientManager.isClientAvailable();
    } catch (error) {
      console.error('Failed to initialize AI client:', error);
      return false;
    }
  }

  /**
   * Get AI feature access status
   */
  getAccessStatus(): {
    allowed: boolean;
    reason?: 'trial' | 'premium' | 'free_tier' | 'limit_exceeded' | 'expired';
    remainingUses?: number;
    statusMessage: string;
  } {
    return this.accessControl.getAccessStatus();
  }

  /**
   * Get current AI model name
   */
  getModelName(): string {
    return this.clientManager.getModelName();
  }

  /**
   * Estimate token usage for a request
   * @deprecated Use AIUtilityService.estimateTokenUsage() instead
   */
  estimateTokenUsage(request: AIAnalysisRequest): number {
    return AIUtilityService.estimateTokenUsage(request);
  }

  /**
   * Update settings and reinitialize if needed
   */
  updateSettings(settings: AISettings): void {
    this.settings = settings;
    this.clientManager.updateSettings(settings);
    this.accessControl.updateSettings(settings);
    this.stateService.updateState({ 
      isInitialized: false, 
      currentModel: null 
    });
  }

  /**
   * Handle streaming analysis with progress updates
   * @deprecated Use streamingService.handleStreamingAnalysis() instead
   */
  private async handleStreamingAnalysis(request: AIAnalysisRequest): Promise<AIAnalysisResult[]> {
    const aiClient = this.clientManager.getClient();
    if (!aiClient) {
      throw new Error('AI client not initialized');
    }
    
    return await this.streamingService.handleStreamingAnalysis(request, aiClient);
  }

  /**
   * Create streaming client with fallback configuration
   */
  async initializeStreamingClient(fallbackModels?: string[]): Promise<void> {
    try {
      await this.clientManager.initializeStreamingClient(fallbackModels);
      
      this.stateService.updateState({ 
        isInitialized: true, 
        currentModel: this.clientManager.getModelName() 
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      this.stateService.updateState({ 
        isInitialized: false, 
        lastError: errorMessage 
      });
      throw error;
    }
  }

  /**
   * Check if current client supports streaming
   */
  supportsStreaming(): boolean {
    return this.clientManager.supportsStreaming();
  }

  /**
   * Cancel ongoing streaming analysis
   */
  cancelStreaming(): void {
    this.streamingService.cancelStreaming();
  }

  /**
   * Get current service state
   */
  getState(): StreamingAIServiceState {
    return this.stateService.getState();
  }

  /**
   * Clear analysis results and reset state
   */
  clearResults(): void {
    this.stateService.clearResults();
  }

  // Private helper methods

  /**
   * @deprecated Use stateService.updateState() instead
   */
  private updateState(updates: Partial<StreamingAIServiceState>): void {
    this.stateService.updateState(updates);
  }

  /**
   * @deprecated Use accessControl.checkAccessPermissions() instead
   */
  private async checkAccessPermissions(): Promise<void> {
    await this.accessControl.checkAccessPermissions();
  }

  /**
   * @deprecated Use AIUtilityService.validateResults() instead
   */
  private validateResults(results: AIAnalysisResult[], expectedCount: number): AIAnalysisResult[] {
    return AIUtilityService.validateResults(results, expectedCount);
  }

  /**
   * @deprecated Use accessControl.updateUsageTracking() instead
   */
  private updateUsageTracking(): void {
    this.accessControl.updateUsageTracking();
  }

  /**
   * Cancel any ongoing AI analysis
   */
  public cancel(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      this.stateService.updateState({ 
        isAnalyzing: false,
        lastError: '분석이 취소되었습니다',
        streaming: undefined
      });
    }
  }
}